import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { auth } from '../../firebase/config';
import { getUserOrganization, saveLlmsTxtAnalysis, getLlmsTxtAnalyses, getDomainScoreHistory } from '../../firebase/firestore';
import { useLlmsTxtAnalysis } from '../../hooks/useLlmsTxtAnalysis';
import {
  FileText,
  Search,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ExternalLink,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  Globe,
  Sparkles,
  History,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar
} from 'lucide-react';

// Priority badge colors
const priorityColors = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
};

// Score color based on value
const getScoreColor = (score) => {
  if (score >= 80) return 'text-green-400';
  if (score >= 60) return 'text-yellow-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
};

// Format date for display
const formatDate = (timestamp) => {
  if (!timestamp) return 'Never';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Calculate score change indicator
const getScoreChange = (currentScore, previousScore) => {
  if (previousScore === null || previousScore === undefined) return null;
  const change = currentScore - previousScore;
  if (change > 0) return { direction: 'up', value: change };
  if (change < 0) return { direction: 'down', value: Math.abs(change) };
  return { direction: 'same', value: 0 };
};


function LlmsTxtAnalyzer() {
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDomains, setExpandedDomains] = useState({});
  const [copiedSuggested, setCopiedSuggested] = useState(false);
  const [activeTab, setActiveTab] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [domainHistory, setDomainHistory] = useState({});
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [displayedAnalysis, setDisplayedAnalysis] = useState(null); // The analysis currently shown

  // eslint-disable-next-line no-unused-vars
  const { analysis: newAnalysis, loading: analyzing, error, analyzedomains, clearAnalysis } = useLlmsTxtAnalysis();

  // Update displayed analysis when new analysis comes in
  useEffect(() => {
    if (newAnalysis) {
      setDisplayedAnalysis(newAnalysis);
    }
  }, [newAnalysis]);

  useEffect(() => {
    loadData();
  }, []);

  // Load history when organization changes or after new analysis
  useEffect(() => {
    if (organization?.id) {
      loadHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id]);

  // Save analysis when new analysis is completed
  useEffect(() => {
    if (newAnalysis && organization?.id) {
      saveAnalysis(newAnalysis);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newAnalysis]);

  const loadData = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      const org = await getUserOrganization(user.uid);
      if (org) {
        setOrganization(org);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (skipDisplayUpdate = false) => {
    if (!organization?.id) return;
    try {
      setLoadingHistory(true);
      const history = await getLlmsTxtAnalyses(organization.id);
      setAnalysisHistory(history);

      // Set the most recent analysis as displayed if we don't have one yet
      if (!skipDisplayUpdate && history.length > 0 && !displayedAnalysis) {
        setDisplayedAnalysis(history[0]);
      }

      // Load domain-specific history for score trends
      if (organization?.domains?.length) {
        const historyMap = {};
        for (const domain of organization.domains) {
          const domainHist = await getDomainScoreHistory(organization.id, domain);
          historyMap[domain] = domainHist;
        }
        setDomainHistory(historyMap);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const saveAnalysis = async (analysisData) => {
    if (!organization?.id) return;
    try {
      const user = auth.currentUser;
      await saveLlmsTxtAnalysis(organization.id, {
        ...analysisData,
        userId: user?.uid
      });
      // Reload history after saving, but don't update displayed analysis (we already have the new one)
      await loadHistory(true);
    } catch (error) {
      console.error('Error saving analysis:', error);
    }
  };

  const handleAnalyze = async () => {
    if (!organization?.domains?.length || !organization?.name) return;
    await analyzedomains(organization.domains, organization.name, organization.industry || '');
  };

  const getPreviousScore = (domain) => {
    const history = domainHistory[domain];
    if (!history || history.length < 2) return null;
    return history[1]?.score || null;
  };

  const toggleDomainExpanded = (domain) => {
    setExpandedDomains(prev => ({
      ...prev,
      [domain]: !prev[domain]
    }));
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSuggested(true);
      setTimeout(() => setCopiedSuggested(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const setDomainTab = (domain, tab) => {
    setActiveTab(prev => ({ ...prev, [domain]: tab }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-primary-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-start justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">llms.txt Analyzer</h1>
          <p className="text-gray-400 max-w-2xl">
            Analyze your llms.txt files to improve AI discoverability. The llms.txt file helps AI engines
            understand your site's purpose and key resources.{' '}
            <a
              href="https://llmstxt.org"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-400 hover:text-primary-300 inline-flex items-center"
            >
              Learn more <ExternalLink className="w-3 h-3 ml-1" />
            </a>
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-all border ${
              showHistory
                ? 'bg-primary-500/20 border-primary-500/30 text-primary-400'
                : 'bg-dark-700/50 border-white/10 text-gray-400 hover:text-white'
            }`}
          >
            <History className="w-5 h-5" />
            <span>History</span>
            {analysisHistory.length > 0 && (
              <span className="bg-primary-500/20 text-primary-400 text-xs px-2 py-0.5 rounded-full">
                {analysisHistory.length}
              </span>
            )}
          </button>
          <button
            onClick={handleAnalyze}
            disabled={analyzing || !organization?.domains?.length}
            className="flex items-center space-x-2 bg-gradient-to-r from-primary-500 to-secondary-500 px-6 py-3 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Analyzing...</span>
              </>
            ) : (
              <>
                <Search className="w-5 h-5" />
                <span>Analyze Domains</span>
              </>
            )}
          </button>
        </div>
      </motion.div>

      {/* Domain List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
      >
        <h2 className="text-lg font-semibold text-white mb-4">Your Domains</h2>
        {organization?.domains?.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {organization.domains.map((domain, index) => (
              <div
                key={index}
                className="flex items-center space-x-2 bg-dark-700/50 border border-white/10 rounded-lg px-4 py-2"
              >
                <Globe className="w-4 h-4 text-primary-400" />
                <span className="text-white">{domain}</span>
                {displayedAnalysis?.domains?.find(d => d.domain === domain) && (
                  displayedAnalysis.domains.find(d => d.domain === domain).hasLlmsTxt ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-400" />
                  )
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400">
            No domains configured. Add domains during onboarding or in settings.
          </p>
        )}
      </motion.div>

      {/* Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start space-x-3"
        >
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-400 font-medium">Analysis Error</h3>
            <p className="text-red-300/80 text-sm">{error}</p>
          </div>
        </motion.div>
      )}

      {/* History View */}
      {showHistory && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <History className="w-5 h-5 text-primary-400" />
              <h2 className="text-lg font-semibold text-white">Analysis History</h2>
            </div>
            {loadingHistory && <Loader2 className="w-5 h-5 text-primary-400 animate-spin" />}
          </div>

          {analysisHistory.length === 0 ? (
            <div className="text-center py-8">
              <History className="w-12 h-12 mx-auto mb-3 text-gray-600" />
              <p className="text-gray-400">No analysis history yet. Run your first analysis to start tracking.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Domain Score Trends */}
              {organization?.domains?.length > 0 && Object.keys(domainHistory).length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-300 mb-3">Score Trends by Domain</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {organization.domains.map((domain) => {
                      const history = domainHistory[domain] || [];
                      const currentScore = history[0]?.score;
                      const previousScore = history[1]?.score;
                      const change = getScoreChange(currentScore, previousScore);

                      return (
                        <div
                          key={domain}
                          className="bg-dark-700/50 border border-white/10 rounded-lg p-4"
                        >
                          <div className="flex items-center space-x-2 mb-2">
                            <Globe className="w-4 h-4 text-primary-400" />
                            <span className="text-white font-medium truncate">{domain}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <span className={`text-2xl font-bold ${getScoreColor(currentScore || 0)}`}>
                                {currentScore !== undefined ? `${currentScore}%` : 'N/A'}
                              </span>
                              {change && (
                                <div className="flex items-center space-x-1 mt-1">
                                  {change.direction === 'up' && (
                                    <>
                                      <TrendingUp className="w-4 h-4 text-green-400" />
                                      <span className="text-xs text-green-400">+{change.value}%</span>
                                    </>
                                  )}
                                  {change.direction === 'down' && (
                                    <>
                                      <TrendingDown className="w-4 h-4 text-red-400" />
                                      <span className="text-xs text-red-400">-{change.value}%</span>
                                    </>
                                  )}
                                  {change.direction === 'same' && (
                                    <>
                                      <Minus className="w-4 h-4 text-gray-400" />
                                      <span className="text-xs text-gray-400">No change</span>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-400">{history.length} analyses</p>
                              {history[0]?.date && (
                                <p className="text-xs text-gray-500">Last: {formatDate(history[0].date)}</p>
                              )}
                            </div>
                          </div>

                          {/* Mini score history chart */}
                          {history.length > 1 && (
                            <div className="mt-3 flex items-end space-x-1 h-8">
                              {history.slice(0, 10).reverse().map((h, i) => (
                                <div
                                  key={i}
                                  className={`flex-1 rounded-t ${
                                    h.score >= 80 ? 'bg-green-500/50' :
                                    h.score >= 60 ? 'bg-yellow-500/50' :
                                    h.score >= 40 ? 'bg-orange-500/50' : 'bg-red-500/50'
                                  }`}
                                  style={{ height: `${Math.max(h.score, 10)}%` }}
                                  title={`${h.score}% - ${formatDate(h.date)}`}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Analysis History List */}
              <div>
                <h3 className="text-sm font-medium text-gray-300 mb-3">Recent Analyses</h3>
                <div className="space-y-2">
                  {analysisHistory.slice(0, 10).map((historyItem) => (
                    <div
                      key={historyItem.id}
                      onClick={() => {
                        setDisplayedAnalysis(historyItem);
                        setShowHistory(false);
                      }}
                      className={`bg-dark-700/50 border rounded-lg p-4 cursor-pointer transition-colors ${
                        displayedAnalysis?.id === historyItem.id
                          ? 'border-primary-500/50 bg-primary-500/5'
                          : 'border-white/10 hover:border-primary-500/30'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          {displayedAnalysis?.id === historyItem.id && (
                            <span className="text-xs bg-primary-500/20 text-primary-400 px-2 py-0.5 rounded">
                              Viewing
                            </span>
                          )}
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-300">{formatDate(historyItem.createdAt)}</span>
                          </div>
                          <span className="text-sm text-gray-400">
                            {historyItem.summary?.domainsAnalyzed || 0} domains
                          </span>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <span className={`text-lg font-bold ${getScoreColor(historyItem.summary?.averageScore || 0)}`}>
                              {historyItem.summary?.averageScore > 0
                                ? `${Math.round(historyItem.summary.averageScore)}%`
                                : 'N/A'}
                            </span>
                            <p className="text-xs text-gray-400">Avg Score</p>
                          </div>
                          <div className="text-right">
                            <span className="text-lg font-bold text-white">
                              {historyItem.summary?.domainsWithLlmsTxt || 0}
                            </span>
                            <p className="text-xs text-gray-400">With llms.txt</p>
                          </div>
                        </div>
                      </div>

                      {/* Domain breakdown */}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {historyItem.domains?.map((d) => (
                          <div
                            key={d.domain}
                            className={`flex items-center space-x-1 px-2 py-1 rounded text-xs ${
                              d.hasLlmsTxt
                                ? 'bg-green-500/10 text-green-400'
                                : 'bg-red-500/10 text-red-400'
                            }`}
                          >
                            {d.hasLlmsTxt ? (
                              <CheckCircle className="w-3 h-3" />
                            ) : (
                              <XCircle className="w-3 h-3" />
                            )}
                            <span>{d.domain}</span>
                            {d.analysis?.overallScore !== undefined && (
                              <span className={`font-medium ${getScoreColor(d.analysis.overallScore)}`}>
                                {d.analysis.overallScore}%
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Analysis Results */}
      {displayedAnalysis && (
        <>
          {/* Summary Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-6"
          >
            <div className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <p className="text-sm text-gray-400 mb-2">Domains Analyzed</p>
              <h3 className="text-3xl font-bold text-white">{displayedAnalysis.summary.domainsAnalyzed}</h3>
            </div>
            <div className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <p className="text-sm text-gray-400 mb-2">With llms.txt</p>
              <h3 className="text-3xl font-bold text-white">{displayedAnalysis.summary.domainsWithLlmsTxt}</h3>
            </div>
            <div className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <p className="text-sm text-gray-400 mb-2">Average Score</p>
              <h3 className={`text-3xl font-bold ${getScoreColor(displayedAnalysis.summary.averageScore)}`}>
                {displayedAnalysis.summary.averageScore > 0 ? `${Math.round(displayedAnalysis.summary.averageScore)}%` : 'N/A'}
              </h3>
            </div>
            <div className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <p className="text-sm text-gray-400 mb-2">Top Priority</p>
              <h3 className="text-lg font-medium text-white truncate">
                {displayedAnalysis.summary.topRecommendations?.[0] || 'None'}
              </h3>
            </div>
          </motion.div>

          {/* Domain Results */}
          <div className="space-y-4">
            {displayedAnalysis.domains.map((domainResult, index) => (
              <motion.div
                key={domainResult.domain}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden"
              >
                {/* Domain Header */}
                <div
                  className="p-6 cursor-pointer hover:bg-dark-700/30 transition-colors"
                  onClick={() => toggleDomainExpanded(domainResult.domain)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        domainResult.hasLlmsTxt
                          ? 'bg-green-500/20'
                          : 'bg-red-500/20'
                      }`}>
                        {domainResult.hasLlmsTxt ? (
                          <CheckCircle className="w-6 h-6 text-green-400" />
                        ) : (
                          <XCircle className="w-6 h-6 text-red-400" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{domainResult.domain}</h3>
                        <p className="text-sm text-gray-400">
                          {domainResult.hasLlmsTxt
                            ? `HTTP ${domainResult.httpStatus} - File found`
                            : domainResult.fetchError || 'No llms.txt file found'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      {domainResult.analysis && (
                        <div className="text-center">
                          <div className="flex items-center space-x-2">
                            <span className={`text-2xl font-bold ${getScoreColor(domainResult.analysis.overallScore)}`}>
                              {domainResult.analysis.overallScore}%
                            </span>
                            {(() => {
                              const prevScore = getPreviousScore(domainResult.domain);
                              const change = getScoreChange(domainResult.analysis.overallScore, prevScore);
                              if (!change) return null;
                              return (
                                <span className="flex items-center">
                                  {change.direction === 'up' && (
                                    <span className="flex items-center text-green-400">
                                      <TrendingUp className="w-4 h-4" />
                                      <span className="text-xs ml-0.5">+{change.value}</span>
                                    </span>
                                  )}
                                  {change.direction === 'down' && (
                                    <span className="flex items-center text-red-400">
                                      <TrendingDown className="w-4 h-4" />
                                      <span className="text-xs ml-0.5">-{change.value}</span>
                                    </span>
                                  )}
                                  {change.direction === 'same' && (
                                    <Minus className="w-4 h-4 text-gray-400" />
                                  )}
                                </span>
                              );
                            })()}
                          </div>
                          <p className="text-xs text-gray-400">Overall Score</p>
                        </div>
                      )}
                      {expandedDomains[domainResult.domain] ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedDomains[domainResult.domain] && domainResult.analysis && (
                  <div className="border-t border-white/10 p-6">
                    {/* Tabs */}
                    <div className="flex space-x-2 mb-6 overflow-x-auto">
                      {['format', 'content', 'geo', 'recommendations'].map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setDomainTab(domainResult.domain, tab)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                            (activeTab[domainResult.domain] || 'format') === tab
                              ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                              : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                          }`}
                        >
                          {tab.charAt(0).toUpperCase() + tab.slice(1)}
                          {tab === 'format' && (
                            <span className={`ml-2 ${getScoreColor(domainResult.analysis.formatCompliance.score)}`}>
                              {domainResult.analysis.formatCompliance.score}%
                            </span>
                          )}
                          {tab === 'content' && (
                            <span className={`ml-2 ${getScoreColor(domainResult.analysis.contentQuality.score)}`}>
                              {domainResult.analysis.contentQuality.score}%
                            </span>
                          )}
                          {tab === 'geo' && (
                            <span className={`ml-2 ${getScoreColor(domainResult.analysis.geoOptimization.score)}`}>
                              {domainResult.analysis.geoOptimization.score}%
                            </span>
                          )}
                          {tab === 'recommendations' && (
                            <span className="ml-2 text-gray-400">
                              ({domainResult.analysis.recommendations.length})
                            </span>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Tab Content */}
                    {(activeTab[domainResult.domain] || 'format') === 'format' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className={`p-4 rounded-lg border ${
                            domainResult.analysis.formatCompliance.hasH1
                              ? 'bg-green-500/10 border-green-500/30'
                              : 'bg-red-500/10 border-red-500/30'
                          }`}>
                            <div className="flex items-center space-x-2 mb-1">
                              {domainResult.analysis.formatCompliance.hasH1 ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-400" />
                              )}
                              <span className="text-sm font-medium text-white">H1 Heading</span>
                            </div>
                            <p className="text-xs text-gray-400">Required</p>
                          </div>
                          <div className={`p-4 rounded-lg border ${
                            domainResult.analysis.formatCompliance.hasBlockquote
                              ? 'bg-green-500/10 border-green-500/30'
                              : 'bg-yellow-500/10 border-yellow-500/30'
                          }`}>
                            <div className="flex items-center space-x-2 mb-1">
                              {domainResult.analysis.formatCompliance.hasBlockquote ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <AlertTriangle className="w-4 h-4 text-yellow-400" />
                              )}
                              <span className="text-sm font-medium text-white">Blockquote</span>
                            </div>
                            <p className="text-xs text-gray-400">Recommended</p>
                          </div>
                          <div className={`p-4 rounded-lg border ${
                            domainResult.analysis.formatCompliance.hasFileLists
                              ? 'bg-green-500/10 border-green-500/30'
                              : 'bg-yellow-500/10 border-yellow-500/30'
                          }`}>
                            <div className="flex items-center space-x-2 mb-1">
                              {domainResult.analysis.formatCompliance.hasFileLists ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <AlertTriangle className="w-4 h-4 text-yellow-400" />
                              )}
                              <span className="text-sm font-medium text-white">File Lists</span>
                            </div>
                            <p className="text-xs text-gray-400">Recommended</p>
                          </div>
                          <div className={`p-4 rounded-lg border ${
                            domainResult.analysis.formatCompliance.hasOptionalSection
                              ? 'bg-green-500/10 border-green-500/30'
                              : 'bg-gray-500/10 border-gray-500/30'
                          }`}>
                            <div className="flex items-center space-x-2 mb-1">
                              {domainResult.analysis.formatCompliance.hasOptionalSection ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <span className="w-4 h-4 rounded-full border border-gray-500" />
                              )}
                              <span className="text-sm font-medium text-white">Optional Section</span>
                            </div>
                            <p className="text-xs text-gray-400">Optional</p>
                          </div>
                        </div>
                        {domainResult.analysis.formatCompliance.issues.length > 0 && (
                          <div className="mt-4">
                            <h4 className="text-sm font-medium text-gray-300 mb-2">Issues</h4>
                            <ul className="space-y-1">
                              {domainResult.analysis.formatCompliance.issues.map((issue, i) => (
                                <li key={i} className="text-sm text-gray-400 flex items-start space-x-2">
                                  <span className="text-red-400">•</span>
                                  <span>{issue}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {(activeTab[domainResult.domain] || 'format') === 'content' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-dark-700/50 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-gray-300 mb-2">Clarity</h4>
                            <p className="text-white">{domainResult.analysis.contentQuality.clarity}</p>
                          </div>
                          <div className="bg-dark-700/50 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-gray-300 mb-2">Completeness</h4>
                            <p className="text-white">{domainResult.analysis.contentQuality.completeness}</p>
                          </div>
                        </div>
                        {domainResult.analysis.contentQuality.issues.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-300 mb-2">Issues</h4>
                            <ul className="space-y-1">
                              {domainResult.analysis.contentQuality.issues.map((issue, i) => (
                                <li key={i} className="text-sm text-gray-400 flex items-start space-x-2">
                                  <span className="text-yellow-400">•</span>
                                  <span>{issue}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {(activeTab[domainResult.domain] || 'format') === 'geo' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-green-400 mb-2">Strengths</h4>
                            <ul className="space-y-1">
                              {domainResult.analysis.geoOptimization.strengths.map((strength, i) => (
                                <li key={i} className="text-sm text-gray-300 flex items-start space-x-2">
                                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                                  <span>{strength}</span>
                                </li>
                              ))}
                              {domainResult.analysis.geoOptimization.strengths.length === 0 && (
                                <li className="text-sm text-gray-400">No strengths identified</li>
                              )}
                            </ul>
                          </div>
                          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-red-400 mb-2">Weaknesses</h4>
                            <ul className="space-y-1">
                              {domainResult.analysis.geoOptimization.weaknesses.map((weakness, i) => (
                                <li key={i} className="text-sm text-gray-300 flex items-start space-x-2">
                                  <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                                  <span>{weakness}</span>
                                </li>
                              ))}
                              {domainResult.analysis.geoOptimization.weaknesses.length === 0 && (
                                <li className="text-sm text-gray-400">No weaknesses identified</li>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}

                    {(activeTab[domainResult.domain] || 'format') === 'recommendations' && (
                      <div className="space-y-3">
                        {domainResult.analysis.recommendations.map((rec, i) => (
                          <div key={i} className="bg-dark-700/50 rounded-lg p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium border ${priorityColors[rec.priority]}`}>
                                  {rec.priority}
                                </span>
                                <span className="text-xs text-gray-400">{rec.category}</span>
                              </div>
                            </div>
                            <h4 className="text-white font-medium mb-1">{rec.issue}</h4>
                            <p className="text-sm text-gray-400 mb-2">{rec.suggestion}</p>
                            {rec.exampleCode && (
                              <pre className="bg-dark-900 rounded-lg p-3 text-xs text-gray-300 overflow-x-auto">
                                <code>{rec.exampleCode}</code>
                              </pre>
                            )}
                          </div>
                        ))}
                        {domainResult.analysis.recommendations.length === 0 && (
                          <div className="text-center py-8">
                            <Sparkles className="w-12 h-12 mx-auto mb-3 text-green-400" />
                            <p className="text-gray-400">No recommendations - your llms.txt looks great!</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Expanded Content - No llms.txt */}
                {expandedDomains[domainResult.domain] && !domainResult.analysis && (
                  <div className="border-t border-white/10 p-6">
                    <div className="text-center py-8">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                      <h3 className="text-lg font-semibold text-white mb-2">No llms.txt file found</h3>
                      <p className="text-gray-400 mb-4">
                        Create an llms.txt file at <code className="bg-dark-700 px-2 py-1 rounded">https://{domainResult.domain}/llms.txt</code>
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Suggested llms.txt */}
          {displayedAnalysis.suggestedLlmsTxt && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-r from-primary-500/10 to-secondary-500/10 border border-primary-500/30 rounded-xl p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Suggested llms.txt</h3>
                    <p className="text-sm text-gray-400">AI-generated template based on your organization</p>
                  </div>
                </div>
                <button
                  onClick={() => copyToClipboard(displayedAnalysis.suggestedLlmsTxt)}
                  className="flex items-center space-x-2 bg-dark-700/50 hover:bg-dark-700 border border-white/10 px-4 py-2 rounded-lg transition-colors"
                >
                  {copiedSuggested ? (
                    <>
                      <Check className="w-4 h-4 text-green-400" />
                      <span className="text-green-400">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-400">Copy</span>
                    </>
                  )}
                </button>
              </div>
              <pre className="bg-dark-900/80 rounded-lg p-4 text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap">
                <code>{displayedAnalysis.suggestedLlmsTxt}</code>
              </pre>
            </motion.div>
          )}
        </>
      )}

      {/* Empty State */}
      {!displayedAnalysis && !analyzing && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-12 text-center"
        >
          <FileText className="w-16 h-16 mx-auto mb-4 text-gray-600" />
          <h3 className="text-xl font-semibold text-white mb-2">Ready to Analyze</h3>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Click "Analyze Domains" to check your llms.txt files and get AI-powered recommendations
            to improve your GEO performance.
          </p>
          <button
            onClick={handleAnalyze}
            disabled={!organization?.domains?.length}
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-primary-500 to-secondary-500 px-6 py-3 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Search className="w-5 h-5" />
            <span>Analyze Domains</span>
          </button>
        </motion.div>
      )}
    </div>
  );
}

export default LlmsTxtAnalyzer;
